import { useEffect, useState } from 'react';

import { api } from '../api.js';
import { toast } from './Toasts.jsx';

// T189 — salary, honestly.
//
// Only ~21% of ingested ads state pay at all, and for Denmark specifically it
// is 41 jobs out of 14k. That is far too thin to derive a "market rate" from,
// and presenting one would manufacture authority the data cannot support — the
// roadmap rules out salary modelling for exactly this reason.
//
// So this panel does two separate things and never blurs them:
//   1. shows what the AD published (Job.extracted.salary), or says plainly that
//      it published nothing;
//   2. lets the operator record what THEY expect — from a recruiter call, their
//      own research, or a hunch — tagged with where it came from.
//
// Keeping the two apart is what makes asked-vs-offered analysis possible later
// (T205). Merging them would destroy the distinction permanently.

const CURRENCIES = ['DKK', 'EUR', 'SEK', 'NOK', 'GBP', 'USD', 'PLN', 'CHF'];
const SOURCES = [
  { value: 'recruiter', label: 'Recruiter said' },
  { value: 'research', label: 'Researched' },
  { value: 'operator', label: 'My estimate' },
];

const fmt = (n) => (n == null ? '' : new Intl.NumberFormat('da-DK').format(n));

export function formatListed(salary) {
  if (!salary || (salary.min == null && salary.max == null)) return null;
  const cur = salary.currency ? `${salary.currency} ` : '';
  const per = salary.period ? ` / ${salary.period}` : '';
  const body =
    salary.min != null && salary.max != null && salary.max !== salary.min
      ? `${fmt(salary.min)}–${fmt(salary.max)}`
      : fmt(salary.min ?? salary.max);
  return `${cur}${body}${per}`;
}

export default function SalaryPanel({ rec, onUpdated }) {
  const listed = formatListed(rec?.job?.extracted?.salary);
  const saved = rec?.salaryExpectation ?? null;

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ min: '', max: '', currency: 'DKK', period: 'month', source: 'recruiter', note: '' });

  // Re-seed when the drawer walks to another job (j/k navigation).
  useEffect(() => {
    setOpen(false);
    setForm({
      min: saved?.min ?? '',
      max: saved?.max ?? '',
      currency: saved?.currency ?? 'DKK',
      period: saved?.period ?? 'month',
      source: saved?.source ?? 'recruiter',
      note: saved?.note ?? '',
    });
  }, [rec?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (form.min === '' && form.max === '') {
      toast('Enter at least one figure, or clear the expectation.', { kind: 'error' });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        min: form.min === '' ? null : Number(form.min),
        max: form.max === '' ? null : Number(form.max),
        currency: form.currency,
        period: form.period,
        source: form.source,
        note: form.note,
      };
      const body = await api.patch(`/recommendations/${rec.id}`, { salaryExpectation: payload });
      onUpdated?.({ salaryExpectation: body?.recommendation?.salaryExpectation ?? null });
      setOpen(false);
      toast('Salary expectation saved', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Could not save', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      const body = await api.patch(`/recommendations/${rec.id}`, { salaryExpectation: null });
      onUpdated?.({ salaryExpectation: body?.recommendation?.salaryExpectation ?? null });
      setOpen(false);
      toast('Expectation cleared', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Could not clear', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const savedLabel = saved
    ? formatListed(saved) + (saved.source ? ` · ${SOURCES.find((s) => s.value === saved.source)?.label ?? saved.source}` : '')
    : null;

  return (
    <section className="mt-4">
      <p className="text-[10px] uppercase tracking-widest text-slate-400">Salary</p>

      <p className="mt-1 text-xs text-slate-300">
        {listed ? (
          <>
            <span className="font-medium">{listed}</span>
            <span className="text-slate-500"> · as published in the ad</span>
          </>
        ) : (
          // Said plainly rather than left blank: "no data" and "we did not look"
          // look identical otherwise, and most ads genuinely publish nothing.
          <span className="text-slate-500">Not published in the ad — most aren’t.</span>
        )}
      </p>

      {savedLabel && !open && (
        <p className="mt-1 text-xs text-slate-300">
          <span className="font-medium text-[var(--c-accent)]">{savedLabel}</span>
          <span className="text-slate-500"> · your expectation</span>
          {saved?.note ? <span className="block text-slate-500">{saved.note}</span> : null}
        </p>
      )}

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-1 text-xs text-[var(--c-accent)] underline decoration-dotted hover:opacity-80"
        >
          {saved ? 'Edit expectation' : '+ Add what you expect'}
        </button>
      ) : (
        <div className="mt-2 space-y-2 rounded-md border border-slate-800 p-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">From</span>
              <input
                type="number" inputMode="numeric" value={form.min}
                onChange={(e) => setForm((f) => ({ ...f, min: e.target.value }))}
                className="mt-0.5 w-full rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-xs"
                aria-label="Expected salary from"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">To</span>
              <input
                type="number" inputMode="numeric" value={form.max}
                onChange={(e) => setForm((f) => ({ ...f, max: e.target.value }))}
                className="mt-0.5 w-full rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-xs"
                aria-label="Expected salary to"
              />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <select
              value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
              className="rounded border border-slate-700 bg-slate-900/60 px-1 py-1 text-xs" aria-label="Currency"
            >
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))}
              className="rounded border border-slate-700 bg-slate-900/60 px-1 py-1 text-xs" aria-label="Period"
            >
              {/* Period is explicit, never inferred: a Danish monthly figure and
                  an annual one differ by 12×, and T185 exists because that
                  ambiguity produced confidently wrong comparisons. */}
              <option value="month">per month</option>
              <option value="year">per year</option>
            </select>
            <select
              value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
              className="rounded border border-slate-700 bg-slate-900/60 px-1 py-1 text-xs" aria-label="Where this came from"
            >
              {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <input
            type="text" value={form.note} maxLength={500}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="e.g. Anna said 48–52k + pension"
            className="w-full rounded border border-slate-700 bg-slate-900/60 px-2 py-1 text-xs"
            aria-label="Note"
          />
          <div className="flex items-center gap-2">
            <button type="button" onClick={save} disabled={busy}
              className="rounded bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-500 disabled:opacity-50">
              {busy ? 'saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setOpen(false)} disabled={busy} className="text-xs text-slate-400 hover:text-slate-200">
              Cancel
            </button>
            {saved && (
              <button type="button" onClick={clear} disabled={busy} className="ml-auto text-xs text-slate-500 hover:text-[var(--c-down)]">
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
